import { Button, Notification, Text, TextInput, Title } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FlowStep,
  useConnectedAccountsFlow,
} from '@/components/ConnectedAccounts/useConnectedAccountsFlow';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useRustyState } from '../../state';

export function DataPrivacyPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const { createLanguageURL } = useLanguageAwareRouting();
  const flow = useConnectedAccountsFlow(api);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteInfo, setDeleteInfo] = useState('');
  const [nostrSign, setNostrSign] = useState<(() => Promise<FlowStep>) | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      const data = await api.exportAccountData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `formshive-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(t('dataPrivacy.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const finishDelete = async () => {
    await useRustyState.getState().logout();
    window.location.replace(createLanguageURL('/'));
  };

  const handleFlowStep = async (step: FlowStep) => {
    switch (step.kind) {
      case 'redirecting':
        // Browser is navigating to the IdP; keep the spinner.
        return;
      case 'email_pending':
        setDeleteInfo(t('connectedAccounts.checkEmail'));
        setDeleting(false);
        return;
      case 'nostr_sign_required':
        setNostrSign(() => step.sign);
        setDeleting(false);
        return;
      case 'deleted':
      case 'completed':
        await finishDelete();
        return;
      case 'error':
        setDeleteError(t(`connectedAccounts.error.${step.code}`, t('dataPrivacy.deleteError')));
        setDeleting(false);
        return;
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== 'DELETE') return;
    // guard against rapid double-submit overwriting the in-flight step-up
    if (deleting) return;
    setDeleting(true);
    setDeleteError('');
    setDeleteInfo('');
    setNostrSign(null);
    try {
      const step = await flow.startDelete(deleteConfirmation);
      await handleFlowStep(step);
    } catch {
      setDeleteError(t('dataPrivacy.deleteError'));
      setDeleting(false);
    }
  };

  const handleNostrSign = async () => {
    if (!nostrSign) return;
    if (deleting) return;
    setDeleting(true);
    setNostrSign(null);
    try {
      const step = await nostrSign();
      await handleFlowStep(step);
    } catch {
      setDeleteError(t('dataPrivacy.deleteError'));
      setDeleting(false);
    }
  };

  return (
    <>
      <Title order={2} mb="xs">
        {t('dataPrivacy.title')}
      </Title>

      <Title order={3} mb="xs" mt="md">
        {t('dataPrivacy.exportTitle')}
      </Title>
      <Text mb="md">{t('dataPrivacy.exportDescription')}</Text>
      {exportError && (
        <Notification color="red" mb="md" onClose={() => setExportError('')}>
          {exportError}
        </Notification>
      )}
      <Button onClick={handleExport} loading={exporting}>
        {t('dataPrivacy.exportButton')}
      </Button>

      <Title order={3} mb="xs" mt="xl" c="red">
        {t('dataPrivacy.deleteTitle')}
      </Title>
      <Text mb="md" c="dimmed">
        {t('dataPrivacy.deleteWarning')}
      </Text>
      {deleteError && (
        <Notification color="red" mb="md" onClose={() => setDeleteError('')}>
          {deleteError}
        </Notification>
      )}
      {deleteInfo && (
        <Notification color="blue" mb="md" onClose={() => setDeleteInfo('')}>
          {deleteInfo}
        </Notification>
      )}
      <TextInput
        label={t('dataPrivacy.deleteConfirmation')}
        placeholder={t('dataPrivacy.typeDelete')}
        value={deleteConfirmation}
        onChange={(e) => setDeleteConfirmation(e.currentTarget.value)}
        mb="md"
        maw={300}
      />
      {nostrSign ? (
        <Button color="red" onClick={handleNostrSign} loading={deleting}>
          {t('connectedAccounts.nostrSignButton')}
        </Button>
      ) : (
        <Button
          color="red"
          onClick={handleDelete}
          loading={deleting}
          disabled={deleteConfirmation !== 'DELETE'}
        >
          {t('dataPrivacy.deleteButton')}
        </Button>
      )}
    </>
  );
}
